/**
 * Browser-compatible credential storage using localStorage
 * Data persists across tabs and browser restarts until explicitly cleared (logout)
 */

const STORAGE_KEY = 'xenon-flex-session'
const ENV_STORAGE_KEY = 'xenon-flex-environment'

export interface WebSession {
  apiKey: string
  apiType: 'bearer' | 'token'
  environment: 'staging' | 'production'
}

/**
 * Get current session from sessionStorage
 */
export function get(): WebSession | null {
  try {
    const data = window.localStorage.getItem(STORAGE_KEY)
    if (!data) return null

    const session = JSON.parse(data) as WebSession

    // Validate session structure
    if (!session.apiKey || !session.apiType || !session.environment) {
      console.warn('[SessionStorage] Invalid session data, clearing')
      clear()
      return null
    }

    return session
  } catch (error) {
    console.error('[SessionStorage] Failed to read session:', error)
    return null
  }
}

/**
 * Store session in sessionStorage
 */
export function set(session: WebSession): void {
  try {
    const data = JSON.stringify(session)
    window.localStorage.setItem(STORAGE_KEY, data)
  } catch (error) {
    console.error('[SessionStorage] Failed to store session:', error)
    throw new Error('Failed to store session')
  }
}

/**
 * Clear session from sessionStorage (logout)
 */
export function clear(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
    // Keep environment preference across logout (user's choice should persist)
    // window.localStorage.removeItem(ENV_STORAGE_KEY) // Intentionally not clearing
    console.log('[SessionStorage] Session cleared')
  } catch (error) {
    console.error('[SessionStorage] Failed to clear session:', error)
  }
}

/**
 * Check if user is authenticated (valid session exists)
 */
export function isAuthenticated(): boolean {
  const session = get()
  return session !== null && session.apiKey.length > 0
}

/**
 * Get base URL for current environment
 */
export function getBaseURL(): string {
  const environment = getEnvironment()
  return environment === 'production'
    ? 'https://api.gridx.de'
    : 'https://api.staging.gridx.de'
}

/**
 * Get current environment
 * Priority order:
 * 1. Active session (user logged in with environment set)
 * 2. SessionStorage preference (explicit user choice via environment switcher)
 * 3. Runtime config from window.ENV (deployment default)
 * 4. Default to staging
 */
export function getEnvironment(): 'staging' | 'production' {
  const session = get()
  if (session) {
    return session.environment
  }

  // Check explicit user preference (set by environment switcher)
  try {
    const envPref = window.localStorage.getItem(ENV_STORAGE_KEY)
    if (envPref === 'production' || envPref === 'staging') {
      return envPref
    }
  } catch (error) {
    console.error('[SessionStorage] Failed to read environment preference:', error)
  }

  // Fall back to runtime environment config (set by Kubernetes deployment)
  try {
    const runtimeEnv = (window as any).ENV?.ENVIRONMENT?.toLowerCase()
    if (runtimeEnv === 'production' || runtimeEnv === 'staging') {
      console.log('[SessionStorage] Using runtime environment:', runtimeEnv)
      return runtimeEnv
    }
  } catch (error) {
    console.error('[SessionStorage] Failed to read runtime environment:', error)
  }

  return 'staging'
}

/**
 * Set environment
 * Can be called before login (stores preference) or after (updates session)
 */
export function setEnvironment(environment: 'staging' | 'production'): void {
  const session = get()

  if (session) {
    // Update existing session
    set({
      ...session,
      environment,
    })
  }

  // Always persist preference so it survives logout
  try {
    window.localStorage.setItem(ENV_STORAGE_KEY, environment)
    console.log('[SessionStorage] Environment preference set:', environment)
  } catch (error) {
    console.error('[SessionStorage] Failed to set environment preference:', error)
  }
}

export const sessionStorage = {
  get,
  set,
  clear,
  isAuthenticated,
  getBaseURL,
  getEnvironment,
  setEnvironment,
}
